/**
 * Appstle → Alpine selection bridge.
 *
 * This theme does NOT submit the product <form> to add to cart. Add-to-cart is
 * driven by Alpine: $store.selection.addToCart() reads $store.selection.sellingPlanId
 * and POSTs it to /cart/add (assets/alpine-stores.js). Appstle's widget (added as
 * an app block in the theme editor) expresses the chosen plan via DOM (a checked
 * [name="selling_plan"] input) and document events. This module bridges the two.
 *
 * Responsibilities:
 *   1. Read Appstle's currently-selected selling plan.
 *   2. Map it to a price (from window.Theme.pageData baked allocations) and push it
 *      into the store via setSellingPlan(planId, price, compareAtPrice).
 *   3. Re-sync on Appstle's selling-plan events and on flavor change.
 *
 * Activation: any product page. The widget only applies to the 1x (Regular)
 * selection — while $store.bundle.isBundle is true (a 2x/4x pack is selected)
 * the bridge leaves the selection alone, because the bundle variant owns the
 * price and carries no selling plan. Exception-safe, so a missing Appstle
 * install never throws.
 *
 * See: snippets/page-data.liquid (bakes sellingPlans),
 *      assets/bundle-store.js ($store.bundle, sync()),
 *      assets/alpine-stores.js (setSellingPlan, addToCart).
 */
(() => {
  'use strict';

  const APPSTLE_EVENTS = [
    'AppstleSubscription:SubscriptionWidget:sellingPlanChanged',
    'AppstleSubscription:SubscriptionWidget:SellingPlanSelected',
    'AppstleSubscription:SubscriptionWidget:SellingPlanDeSelected',
  ];

  const log = (...args) => {
    try { if (window.Theme && window.Theme.debug) console.log('[appstle-bridge]', ...args); } catch (_) {}
  };

  /** Only active on product pages. */
  const isProductPage = () => {
    try { return !!(window.Theme && window.Theme.pageData && window.Theme.pageData.product); }
    catch (_) { return false; }
  };

  /** True while a 2x/4x pack is selected — the bundle variant owns price + plan. */
  const isBundleSelected = () => {
    try {
      const b = window.Alpine && window.Alpine.store && window.Alpine.store('bundle');
      return !!(b && b.isBundle);
    } catch (_) { return false; }
  };

  const selectionStore = () => {
    try { return window.Alpine && window.Alpine.store && window.Alpine.store('selection'); }
    catch (_) { return null; }
  };

  const trimVal = (el) => (el && el.value != null && String(el.value).trim()) || '';

  /**
   * Read Appstle's selected selling plan id, covering its widget variants:
   *   - radio buttons (source of truth per Appstle docs): a CHECKED input
   *   - dropdown: a <select name="selling_plan">
   *   - a hidden input Appstle writes the chosen plan into
   * An UNCHECKED radio is deliberately ignored (that's the one-time state).
   * @returns {string} plan id, or '' for one-time / none.
   */
  const readSelectedPlanId = () => {
    try {
      const checked =
        document.querySelector('.appstle_sub_widget input[name="selling_plan"]:checked') ||
        document.querySelector('input[name="selling_plan"]:checked');
      if (checked) return trimVal(checked);

      const select = document.querySelector('select[name="selling_plan"]');
      if (select) return trimVal(select);

      const hidden = document.querySelector('input[type="hidden"][name="selling_plan"]');
      return trimVal(hidden);
    } catch (_) {
      return '';
    }
  };

  /**
   * Dosage days for the 1x selection = servings the customer gets, used to turn a
   * total price into a per-serving (= per-day) figure. Baked by page-data.liquid
   * (30 by default, 10 for travel packs). The subscription is always single-pack,
   * so packQty is 1.
   */
  const totalDaysForProduct = () => {
    try {
      const p = window.Theme && window.Theme.pageData && window.Theme.pageData.product;
      return (p && p.servingsPerPack) || 30;
    } catch (_) {
      return 30;
    }
  };

  /**
   * Recalculate the per-serving (= per-day) price and the save% from a total price.
   * When a subscription plan changes the total, the "€x/serving" line, the per-day
   * pill and the "SAVE n%" badge are all re-derived by dividing by the dosage days.
   *
   * @param {number} priceCents       - the selected plan's total price, in cents
   * @param {number|null} compareCents - the compare-at price, in cents (or null)
   * @param {number} totalDays         - servings/days to divide by (from totalDaysForProduct)
   * @returns {{ perServingCents: number, savePct: number }}
   *
   * Mirrors the SSR Liquid in cart-card-header.liquid (`v.price | divided_by: d0`),
   * which uses integer FLOOR division — so we floor here too and SSR/JS agree to the
   * cent (no off-by-one flicker when Alpine overwrites the baked value).
   */
  const recalcDerived = (priceCents, compareCents, totalDays) => {
    // Shared with $store.bundle.sync() (assets/bundle-store.js publishes it) so
    // a plan change and a pack-size click derive the header's numbers identically.
    if (typeof window.Theme?.recalcDerived === 'function') return window.Theme.recalcDerived(priceCents, compareCents, totalDays);
    const days = totalDays > 0 ? totalDays : 30;
    // Floor to mirror Liquid's `divided_by` (integer/truncating) so the JS result
    // lands on the same cent the server would have rendered — no off-by-one flicker.
    const perServingCents = Math.floor(priceCents / days);
    const savePct =
      compareCents && compareCents > priceCents
        ? Math.floor(((compareCents - priceCents) * 100) / compareCents)
        : 0;
    return { perServingCents, savePct };
  };

  /**
   * Re-fire the hero's price-derived displays (top total, per-serving, per-day,
   * save% badge) after a plan change. Uses a dedicated `store:price-recalculated`
   * signal — NOT `store:card-selected` — so the gallery (which also listens to the
   * latter and scrolls to slide 0) is left undisturbed. Header + per-day consume it.
   */
  const emitPriceRecalc = (store, price, compareAt) => {
    try {
      const fmt = window.Theme && window.Theme.formatMoney;
      if (typeof fmt !== 'function' || price == null) return;
      const totalDays = totalDaysForProduct();
      const { perServingCents, savePct } = recalcDerived(price, compareAt, totalDays);
      window.Theme.event('store:price-recalculated', {
        productId: store.productId,
        variantId: store.variantId,
        packQty: 1,
        totalDays,
        perServing: fmt(perServingCents),
        priceFormatted: fmt(price),
        compareFormatted: compareAt ? fmt(compareAt) : '',
        savePct,
      });
    } catch (_) {}
  };

  /** Current variant object from baked page data, or null. */
  const currentVariant = (store) => {
    try {
      const p = window.Theme && window.Theme.pageData && window.Theme.pageData.product;
      if (!p || !Array.isArray(p.variants)) return null;
      const id = (store && store.variantId) || p.currentVariantId;
      return p.variants.find((v) => v.id === id) || null;
    } catch (_) {
      return null;
    }
  };

  /**
   * Push the currently-selected plan (or one-time) into the selection store with
   * the correct price, so the hero price display stays accurate.
   */
  const syncFromAppstle = () => {
    const store = selectionStore();
    if (!store) return;
    // A bundle pack is selected: its variant is priced as-is and has no plan.
    // The widget is hidden (x-show) but its radios still exist in the DOM, so
    // without this guard a stray mutation would re-apply the plan to the bundle.
    if (isBundleSelected()) { log('bundle selected — skip'); return; }

    const planIdRaw = readSelectedPlanId();
    const variant = currentVariant(store);
    const basePrice = variant ? variant.price : store.price;
    const baseCompareAt = variant ? variant.compareAtPrice : store.compareAtPrice;

    if (!planIdRaw) {
      // One-time purchase.
      store.setSellingPlan(null, basePrice, baseCompareAt);
      emitPriceRecalc(store, basePrice, baseCompareAt);
      log('one-time', { price: basePrice });
      return;
    }

    // Subscription: look up the plan's price in baked allocations; if absent
    // (e.g. allocations not exposed for this variant), keep base price — the cart
    // re-fetch will correct the displayed total after add.
    let price = basePrice;
    let compareAt = baseCompareAt;
    try {
      const plan = variant && Array.isArray(variant.sellingPlans)
        ? variant.sellingPlans.find((sp) => String(sp.id) === String(planIdRaw))
        : null;
      if (plan) {
        price = plan.price;
        compareAt = plan.compareAtPrice;
      }
    } catch (_) {}

    const planId = Number(planIdRaw) || planIdRaw;
    store.setSellingPlan(planId, price, compareAt);
    emitPriceRecalc(store, price, compareAt);
    log('subscription', { planId, price });
  };

  // Debounced re-sync so bursts of Appstle DOM mutations collapse to one update.
  let raf = 0;
  const scheduleSync = () => {
    if (raf) return;
    raf = requestAnimationFrame(() => { raf = 0; syncFromAppstle(); });
  };

  const wire = () => {
    if (!isProductPage()) { log('not a product page — bridge idle'); return; }

    // 1. Appstle's own selection events (dispatched on document).
    APPSTLE_EVENTS.forEach((name) => {
      try { document.addEventListener(name, scheduleSync); } catch (_) {}
    });

    // 2. Delegated change on any selling-plan input (Appstle radios).
    try {
      document.addEventListener('change', (e) => {
        try { if (e.target && e.target.matches && e.target.matches('input[name="selling_plan"]')) scheduleSync(); }
        catch (_) {}
      }, true);
    } catch (_) {}

    // 3. Selection change: $store.bundle.sync() resets sellingPlanId to null and
    //    dispatches store:variant-changed (flavor) / store:card-selected (pack
    //    size). Appstle re-renders plans for the new variant; re-read after it
    //    settles so returning to 1x re-applies the widget's plan.
    try { window.addEventListener('store:variant-changed', scheduleSync); } catch (_) {}
    try { window.addEventListener('store:card-selected', scheduleSync); } catch (_) {}

    // 4. Catch Appstle's widget appearing/changing in the DOM (e.g. late injection).
    try {
      const observer = new MutationObserver(scheduleSync);
      observer.observe(document.body, { childList: true, subtree: true });
    } catch (_) {}

    // 5. Initial sync once Appstle has had a tick to render.
    scheduleSync();
    log('bridge wired');
  };

  // Wait for Alpine's selection store before wiring.
  const start = () => {
    if (selectionStore()) { wire(); return; }
    let tries = 0;
    const timer = setInterval(() => {
      tries += 1;
      if (selectionStore()) { clearInterval(timer); wire(); }
      else if (tries > 100) { clearInterval(timer); log('selection store never appeared — giving up'); }
    }, 50);
  };

  try {
    document.addEventListener('alpine:initialized', start, { once: true });
    // alpine:initialized may already have fired before this module loads.
    if (selectionStore()) start();
  } catch (_) {
    start();
  }
})();
